/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// CLERK CONTEXT HOOK - Foundation for all auth-dependent hooks
// ============================================================================

import { useOrganization } from "@/contexts/organization-context";
import { useUserSafe as useUser } from "@/hooks/auth/use-user-safe";
import { useMemo } from "react";

// ============================================================================
// TYPES
// ============================================================================

export interface DatabaseContext {
  userId: string;
  organizationId: string;
}

export interface ClerkContextState {
  // Auth state
  isLoaded: boolean;
  isSignedIn: boolean;
  user: ReturnType<typeof useUser>["user"];

  // Organization state
  organization: null; // We don't use Clerk's organization anymore
  organizationId: string | null;

  // Context for services
  databaseContext: DatabaseContext | null;

  // Loading states
  authLoading: boolean;
  orgLoading: boolean;

  // Error states
  authError: string | null;
  orgError: string | null;
}

export interface ClerkContextActions {
  // Validation helpers
  hasOrganization: boolean;
  hasUser: boolean;
  isValid: boolean;

  // Error helpers
  hasErrors: boolean;
  getErrorMessage: () => string | null;

  // State helpers
  isReady: boolean;
  canPerformOperations: boolean;
}

export type UseClerkContextReturn = ClerkContextState & ClerkContextActions;

// ============================================================================
// CLERK CONTEXT HOOK - Public export
// ============================================================================
// NOTE: Logic is inlined here (removed separate internal functions) to allow
// conditional hook calls based on test mode

export const useClerkContext = (): UseClerkContextReturn => {
  // Always call OrganizationProvider hook (safe in test mode)
  const { organizationId } = useOrganization();

  // useUserSafe handles test mode internally, so this is safe to call
  const { user, isLoaded: authLoaded, isSignedIn } = useUser();

  const isLoading = false; // organizationId already loaded above
  const isLoaded = authLoaded && !isLoading;
  const authLoading = !authLoaded;

  // Always call useMemo (required by React rules)
  const authError = useMemo(() => {
    if (authLoading) return null;
    if (!isSignedIn) return "User not authenticated";
    if (!user) return "User not found";
    return null;
  }, [authLoading, isSignedIn, user]);

  const orgError = useMemo(() => {
    if (isLoading) return null;
    if (!organizationId) return null;
    return null;
  }, [isLoading, organizationId]);

  const databaseContext = useMemo((): DatabaseContext | null => {
    if (!isSignedIn || !user?.id || !organizationId) {
      return null;
    }
    return {
      userId: user.id,
      organizationId: organizationId,
    };
  }, [isSignedIn, user?.id, organizationId]);

  const hasUser = Boolean(isSignedIn) && Boolean(user?.id);
  const hasOrganization = Boolean(organizationId);
  const isValid = hasUser && !authError;
  const hasErrors = !!(authError ?? orgError);
  const isReady = isLoaded && !authLoading && !isLoading;
  const canPerformOperations = isReady && isValid;

  const getErrorMessage = (): string | null => {
    if (authError) return authError;
    if (orgError) return orgError;
    return null;
  };

  return {
    isLoaded,
    isSignedIn: Boolean(isSignedIn),
    user,
    organization: null,
    organizationId: organizationId,
    databaseContext,
    authLoading,
    orgLoading: isLoading,
    authError,
    orgError,
    hasOrganization,
    hasUser,
    isValid,
    hasErrors,
    getErrorMessage,
    isReady,
    canPerformOperations,
  };
};
